import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../../../config/firebase";
import { doc, collection, getDocs, updateDoc } from "firebase/firestore";
import { stepConfigs } from "../../../../_start/partials/widgets/forms/stepConfig";
import { useSubscription } from "../../../../hooks/useSubscription";
import { UpgradePrompt } from "../../../../_start/partials/widgets/engage/UpgradePrompt";

interface LocationState {
  userID: string;
  projectID: string;
  projectName: string;
  projectDescription: string;
  overallScore?: number;
  categoryScores?: Record<string, number>;
}

interface StepData {
  stepNumber: number;
  title: string;
  category: string;
  formData: Record<string, string>;
  confidence: "high" | "medium" | "low";
  needsReview: boolean;
  aiGenerated: boolean;
}

interface CategoryGroup {
  id: string;
  label: string;
  icon: string;
  color: string;
  iconColor: string;
  steps: StepData[];
  score?: number;
}

const CATEGORIES = [
  { id: "marketOpportunity", label: "Market Opportunity", icon: "bi-graph-up-arrow", color: "primary", iconColor: "#3B82F6", stepRange: [1, 5] as [number, number] },
  { id: "customerUnderstanding", label: "Customer Understanding", icon: "bi-people", color: "info", iconColor: "#8B5CF6", stepRange: [6, 10] as [number, number] },
  { id: "valueAndProduct", label: "Value & Product", icon: "bi-box-seam", color: "success", iconColor: "#EC4899", stepRange: [11, 14] as [number, number] },
  { id: "businessModel", label: "Business Model", icon: "bi-currency-dollar", color: "warning", iconColor: "#F59E0B", stepRange: [15, 19] as [number, number] },
  { id: "validationExecution", label: "Validation & Execution", icon: "bi-check2-circle", color: "danger", iconColor: "#10B981", stepRange: [20, 24] as [number, number] },
];

// Key fields that need human review per step
const CRITICAL_FIELDS: Record<number, string[]> = {
  1: ["marketSegments", "selectedSegment"],
  2: ["beachheadMarket", "whyThisMarket"],
  3: ["endUserProfile", "demographics"],
  4: ["tamCalculation", "marketSize"],
  5: ["personaName", "personaGoals", "personaPains"],
  6: ["useCaseDescription", "customerJourney"],
  7: ["productFeatures", "mvpFeatures"],
  8: ["valueProposition", "quantifiedBenefit"],
  9: ["firstTenCustomers", "acquisitionStrategy"],
  10: ["coreCompetency", "unfairAdvantage"],
  15: ["businessModel", "revenueStreams"],
  16: ["pricingStrategy", "pricePoint"],
  20: ["keyAssumptions", "riskiestAssumption"],
  22: ["mvpDefinition", "mvpFeatures"],
};

// Assumptions the AI commonly makes
const AI_ASSUMPTIONS: Record<number, string> = {
  1: "We identified market segments based on your description. Verify these match your research.",
  2: "We selected the beachhead market with the best fit. Confirm this aligns with your strategy.",
  4: "TAM is estimated. Replace with your own market research for accuracy.",
  5: "Persona details are generated. Update with real customer interview insights.",
  8: "Value quantification is estimated. Validate with customer feedback.",
  15: "Business model inferred from your description. Confirm revenue model accuracy.",
  16: "Pricing is suggested based on similar businesses. Adjust for your market.",
  17: "LTV calculated with assumptions. Update with your actual metrics.",
  19: "COCA estimated. Refine with your actual acquisition costs.",
};

const AIReviewPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canUseFeature } = useSubscription();
  const { projectID, userID, projectName, projectDescription, overallScore, categoryScores } =
    (location.state || {}) as LocationState;

  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(0);
  const [reviewedSteps, setReviewedSteps] = useState<Set<number>>(new Set());
  const [editingField, setEditingField] = useState<{ step: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState("");

  // Fetch all step data
  const fetchStepData = useCallback(async () => {
    if (!userID || !projectID) return;

    try {
      const userRef = doc(db, "users", userID);
      const projectRef = doc(userRef, "projects", projectID);
      const progressRef = collection(projectRef, "progress");
      const snapshot = await getDocs(progressRef);

      const stepsMap: Record<number, Record<string, string>> = {};
      snapshot.docs.forEach((doc) => {
        const stepNum = parseInt(doc.id.replace("step", ""), 10);
        const data = doc.data();
        stepsMap[stepNum] = data.form || {};
      });

      // Build category groups
      const groups: CategoryGroup[] = CATEGORIES.map((cat) => {
        const steps: StepData[] = [];
        for (let i = cat.stepRange[0]; i <= cat.stepRange[1]; i++) {
          const config = stepConfigs[i];
          const formData = stepsMap[i] || {};
          const filledFields = Object.values(formData).filter((v) => v && v.trim()).length;

          // Determine confidence based on content quality
          let confidence: "high" | "medium" | "low" = "high";
          const criticalFields = CRITICAL_FIELDS[i] || [];
          const criticalFilled = criticalFields.filter((f) => formData[f] && formData[f].trim().length > 20).length;

          if (criticalFilled < criticalFields.length * 0.5) {
            confidence = "low";
          } else if (criticalFilled < criticalFields.length * 0.8) {
            confidence = "medium";
          }

          // Mark for review if has AI assumption or low confidence
          const needsReview = AI_ASSUMPTIONS[i] !== undefined || confidence !== "high";

          steps.push({
            stepNumber: i,
            title: config?.title || `Step ${i}`,
            category: cat.id,
            formData,
            confidence,
            needsReview,
            aiGenerated: filledFields > 0,
          });
        }

        return {
          ...cat,
          steps,
          score: categoryScores?.[cat.id as keyof typeof categoryScores],
        };
      });

      setCategoryGroups(groups);
    } catch (error) {
      console.error("Error fetching step data:", error);
    } finally {
      setLoading(false);
    }
  }, [userID, projectID, categoryScores]);

  useEffect(() => {
    fetchStepData();
  }, [fetchStepData]);

  const handleMarkReviewed = (stepNumber: number) => {
    setReviewedSteps((prev) => new Set([...prev, stepNumber]));
  };

  const handleEditField = (step: number, field: string, currentValue: string) => {
    setEditingField({ step, field });
    setEditValue(currentValue);
  };

  const handleSaveEdit = async () => {
    if (!editingField || !userID || !projectID) return;

    try {
      const userRef = doc(db, "users", userID);
      const projectRef = doc(userRef, "projects", projectID);
      const stepRef = doc(projectRef, "progress", `step${editingField.step}`);

      // Get current form data and update
      const currentGroup = categoryGroups.find((g) =>
        g.steps.some((s) => s.stepNumber === editingField.step)
      );
      const currentStep = currentGroup?.steps.find((s) => s.stepNumber === editingField.step);

      if (currentStep) {
        const updatedForm = { ...currentStep.formData, [editingField.field]: editValue };
        await updateDoc(stepRef, {
          form: updatedForm,
          humanEdited: true,
          editedAt: new Date().toISOString(),
        });

        // Update local state
        setCategoryGroups((prev) =>
          prev.map((cat) => ({
            ...cat,
            steps: cat.steps.map((s) =>
              s.stepNumber === editingField.step
                ? { ...s, formData: updatedForm }
                : s
            ),
          }))
        );

        handleMarkReviewed(editingField.step);
      }
    } catch (error) {
      console.error("Error saving edit:", error);
    }

    setEditingField(null);
    setEditValue("");
  };

  const handleContinue = () => {
    navigate("/project", {
      state: {
        projectID,
        userID,
        projectTitle: projectName,
        projectDescription,
      },
    });
  };

  const totalStepsNeedingReview = categoryGroups.reduce(
    (sum, cat) => sum + cat.steps.filter((s) => s.needsReview && !reviewedSteps.has(s.stepNumber)).length,
    0
  );

  const getConfidenceBadge = (confidence: "high" | "medium" | "low") => {
    const styles = {
      high: { bg: "#D1FAE5", text: "#059669" },
      medium: { bg: "#FEF3C7", text: "#B45309" },
      low: { bg: "#FEE2E2", text: "#DC2626" },
    };
    const labels = {
      high: "High Confidence",
      medium: "Needs Review",
      low: "Low Confidence",
    };
    const icons = {
      high: "bi-check-circle",
      medium: "bi-exclamation-circle",
      low: "bi-question-circle",
    };
    const s = styles[confidence];
    return (
      <span style={{
        backgroundColor: s.bg,
        color: s.text,
        borderRadius: "4px",
        padding: "2px 8px",
        fontSize: "11px",
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}>
        <i className={`bi ${icons[confidence]}`}></i>
        {labels[confidence]}
      </span>
    );
  };

  if (!projectID || !userID) {
    return (
      <div className="card">
        <div className="card-body text-center py-15">
          <i className="bi bi-exclamation-triangle text-warning fs-2x mb-4"></i>
          <h3>Missing Project Information</h3>
          <button className="btn btn-primary mt-4" onClick={() => navigate("/dashboard")}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-4" style={{ width: "3rem", height: "3rem" }}></div>
          <p className="text-muted">Loading AI analysis for review...</p>
        </div>
      </div>
    );
  }

  if (!canUseFeature("ai_review")) {
    return (
      <div className="row justify-content-center">
        <div className="col-lg-6 col-md-8">
          <UpgradePrompt requiredTier="pro" featureName="AI Review" className="mt-10" />
        </div>
      </div>
    );
  }

  const currentCategory = categoryGroups[activeCategory];

  return (
    <div className="row">
      {/* Header Card */}
      <div className="col-12 mb-5">
        <div className="card bg-light-primary">
          <div className="card-body py-6">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div className="d-flex align-items-center">
                <div className="symbol symbol-70px me-5">
                  <div className="symbol-label bg-primary">
                    <i className="bi bi-robot text-white fs-2x"></i>
                  </div>
                </div>
                <div>
                  <h2 className="fw-bolder mb-1">Review AI Analysis</h2>
                  <p className="text-gray-600 mb-0">
                    AI has populated your validation framework. Review key insights and make them yours.
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center mt-4 mt-lg-0">
                {overallScore && (
                  <div className="d-flex align-items-center me-6">
                    <div className="d-flex flex-column align-items-center me-4">
                      <span className="fs-2x fw-bolder text-primary">{overallScore}</span>
                      <span className="text-muted fs-8">Business Score</span>
                    </div>
                  </div>
                )}
                <div className="d-flex flex-column align-items-center">
                  <span className="fs-3 fw-bolder text-warning">{totalStepsNeedingReview}</span>
                  <span className="text-muted fs-8">Items to Review</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="col-xl-3 mb-5">
        <div className="card">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title fw-bolder">Categories</h3>
          </div>
          <div className="card-body pt-0">
            {categoryGroups.map((cat, index) => {
              const needsReviewCount = cat.steps.filter(
                (s) => s.needsReview && !reviewedSteps.has(s.stepNumber)
              ).length;
              const isActive = activeCategory === index;

              return (
                <div
                  key={cat.id}
                  className="d-flex align-items-center p-4 rounded mb-3"
                  style={{
                    backgroundColor: isActive ? "#DBEAFE" : "#F8FAFC",
                    cursor: "pointer",
                  }}
                  onClick={() => setActiveCategory(index)}
                >
                  <div className="symbol symbol-40px me-3">
                    <div className="symbol-label" style={{ backgroundColor: isActive ? "#DBEAFE" : "#F1F5F9" }}>
                      <i className={`bi ${cat.icon} fs-4`} style={{ color: cat.iconColor }}></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <span className="fw-bold d-block" style={{ color: isActive ? "#1D4ED8" : "#64748B" }}>
                      {cat.label}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      {cat.score !== undefined && (
                        <span style={{ fontSize: "12px", color: "#64748B" }}>{cat.score}%</span>
                      )}
                      {needsReviewCount > 0 && (
                        <span style={{
                          backgroundColor: "#FEF3C7",
                          color: "#B45309",
                          borderRadius: "9999px",
                          fontSize: "10px",
                          fontWeight: 600,
                          padding: "1px 6px",
                        }}>
                          {needsReviewCount}
                        </span>
                      )}
                    </div>
                  </div>
                  {needsReviewCount === 0 && (
                    <i className="bi bi-check-circle-fill" style={{ color: "#10B981" }}></i>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card mt-5">
          <div className="card-body">
            <button
              className="btn btn-primary w-100 mb-3"
              onClick={handleContinue}
            >
              <i className="bi bi-arrow-right me-2"></i>
              Continue to Dashboard
            </button>
            <p className="text-muted text-center fs-8 mb-0">
              You can always come back to edit later
            </p>
          </div>
        </div>
      </div>

      {/* Step Review Content */}
      <div className="col-xl-9">
        <div className="card">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bolder">{currentCategory?.label}</span>
              <span className="text-muted fs-7 mt-1">
                {currentCategory?.steps.filter((s) => s.aiGenerated).length} steps populated by AI
              </span>
            </h3>
            <div className="card-toolbar">
              <span style={{
                backgroundColor: "#F1F5F9",
                color: "#475569",
                borderRadius: "4px",
                padding: "3px 10px",
                fontSize: "12px",
                fontWeight: 500,
              }}>
                {currentCategory?.steps.filter((s) => reviewedSteps.has(s.stepNumber)).length} / {currentCategory?.steps.length} reviewed
              </span>
            </div>
          </div>
          <div className="card-body pt-3">
            {currentCategory?.steps.map((step) => {
              const isReviewed = reviewedSteps.has(step.stepNumber);
              const assumption = AI_ASSUMPTIONS[step.stepNumber];
              const criticalFields = CRITICAL_FIELDS[step.stepNumber] || [];
              const config = stepConfigs[step.stepNumber];

              return (
                <div
                  key={step.stepNumber}
                  className="p-5 mb-4"
                  style={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    backgroundColor: isReviewed ? "#F0FDF4" : "#FFFFFF",
                  }}
                >
                  {/* Step Header */}
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                      {/* Neutral step number badge */}
                      <span style={{
                        backgroundColor: "#F1F5F9",
                        color: "#475569",
                        width: "24px",
                        height: "24px",
                        borderRadius: "9999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginRight: "12px",
                      }}>
                        {step.stepNumber}
                      </span>
                      <div>
                        {/* Step title + reviewed checkmark */}
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h5 className="fw-bolder mb-0">{step.title}</h5>
                          {isReviewed && (
                            <i className="bi bi-check-circle-fill" style={{ color: "#10B981", fontSize: "14px" }}></i>
                          )}
                        </div>
                        {/* Confidence badge only */}
                        {getConfidenceBadge(step.confidence)}
                      </div>
                    </div>
                    <div>
                      <button
                        className="btn btn-sm btn-light-primary me-2"
                        onClick={() => navigate(`/step${step.stepNumber}`, {
                          state: { projectTitle: projectName, projectID, userID }
                        })}
                      >
                        <i className="bi bi-pencil me-1"></i>Edit Full Step
                      </button>
                      {!isReviewed && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleMarkReviewed(step.stepNumber)}
                        >
                          <i className="bi bi-check me-1"></i>Looks Good
                        </button>
                      )}
                    </div>
                  </div>

                  {/* AI Assumption Warning */}
                  {assumption && !isReviewed && (
                    <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mb-4">
                      <i className="bi bi-robot text-warning me-3 fs-3"></i>
                      <div>
                        <h6 className="fw-bold text-gray-800 mb-1">AI Made an Assumption</h6>
                        <p className="text-gray-600 mb-0 fs-7">{assumption}</p>
                      </div>
                    </div>
                  )}

                  {/* Key Fields Preview */}
                  <div className="row g-4">
                    {criticalFields.slice(0, 4).map((fieldKey) => {
                      const fieldConfig = config?.sections?.flatMap((s) => s.fields).find((f) => f.name === fieldKey);
                      const value = step.formData[fieldKey] || "";
                      const isEditing = editingField?.step === step.stepNumber && editingField?.field === fieldKey;

                      return (
                        <div key={fieldKey} className="col-md-6">
                          <div style={{ backgroundColor: "#F8FAFC", borderRadius: "8px", padding: "8px 12px" }}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <label className="fw-bold fs-7" style={{ color: "#475569" }}>
                                {fieldConfig?.label || fieldKey}
                              </label>
                              {!isEditing && (
                                <button
                                  className="btn btn-icon btn-sm btn-light"
                                  onClick={() => handleEditField(step.stepNumber, fieldKey, value)}
                                >
                                  <i className="bi bi-pencil fs-8"></i>
                                </button>
                              )}
                            </div>
                            {isEditing ? (
                              <div>
                                <textarea
                                  className="form-control form-control-sm mb-2"
                                  rows={3}
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  autoFocus
                                />
                                <div className="d-flex gap-2">
                                  <button className="btn btn-sm btn-primary" onClick={handleSaveEdit}>
                                    Save
                                  </button>
                                  <button className="btn btn-sm btn-light" onClick={() => setEditingField(null)}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p style={{ color: "#64748B", fontSize: "13px", marginBottom: 0, minHeight: "40px" }}>
                                {value ? (
                                  value.length > 150 ? value.substring(0, 150) + "..." : value
                                ) : (
                                  <span style={{ color: "#94A3B8", fontStyle: "italic" }}>Not filled</span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Show more fields indicator */}
                  {Object.keys(step.formData).length > 4 && (
                    <div className="text-center mt-3">
                      <button
                        className="btn btn-sm btn-link text-primary"
                        onClick={() => navigate(`/step${step.stepNumber}`, {
                          state: { projectTitle: projectName, projectID, userID }
                        })}
                      >
                        +{Object.keys(step.formData).length - 4} more fields
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="d-flex justify-content-between mt-5">
          <button
            className="btn btn-light"
            disabled={activeCategory === 0}
            onClick={() => setActiveCategory((prev) => prev - 1)}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Previous Category
          </button>
          {activeCategory < categoryGroups.length - 1 ? (
            <button
              className="btn btn-primary"
              onClick={() => setActiveCategory((prev) => prev + 1)}
            >
              Next Category
              <i className="bi bi-arrow-right ms-2"></i>
            </button>
          ) : (
            <button className="btn btn-success" onClick={handleContinue}>
              <i className="bi bi-check-circle me-2"></i>
              Complete Review & Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { AIReviewPage };
